// @tennis-matches/ui-components/loading-overlay
// Event-based API for LoadingOverlay – works from anywhere (inside/outside React).
// Loader appears only after delayMs (default 250ms). When shown, stays visible at least minShowMs.

const START_EVENT = "loading-overlay:start";
const STOP_EVENT = "loading-overlay:stop";

const DEFAULT_DELAY_MS = 250;
const DEFAULT_MIN_SHOW_MS = 300;

function createToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Dispatch start-loading event. Returns token for stopLoading(token).
 * Loader appears only after delayMs; when shown, minShowMs prevents flicker.
 * @param {string} [message] – Optional i18n key for message
 * @param {{ delayMs?: number, minShowMs?: number }} [options]
 * @returns {string} token – Pass to stopLoading(token)
 */
export function startLoading(message, options = {}) {
  if (typeof window === "undefined") return "";
  const token = createToken();
  const delayMs = options?.delayMs ?? DEFAULT_DELAY_MS;
  const minShowMs = options?.minShowMs ?? DEFAULT_MIN_SHOW_MS;
  window.dispatchEvent(
    new CustomEvent(START_EVENT, { detail: { token, message, delayMs, minShowMs } })
  );
  return token;
}

/**
 * Dispatch stop-loading event for the given token.
 * Cancels pending timer if overlay not yet visible.
 * @param {string} token – From startLoading()
 */
export function stopLoading(token) {
  if (typeof window === "undefined") return;
  if (!token) return;
  window.dispatchEvent(new CustomEvent(STOP_EVENT, { detail: { token } }));
}

/**
 * Wrap an async function with loading overlay.
 * Loader appears only after delayMs; minShowMs avoids flicker when action is quick.
 * @param {() => Promise<any>} asyncFn
 * @param {string} [message] – Optional i18n key
 * @param {{ delayMs?: number, minShowMs?: number }} [options] – Defaults: delayMs 250, minShowMs 400
 * @returns {Promise<any>}
 */
export async function withLoading(asyncFn, message, options = {}) {
  if (typeof asyncFn !== "function") return;
  const delayMs = options?.delayMs ?? DEFAULT_DELAY_MS;
  const minShowMs = options?.minShowMs ?? 400;
  const token = startLoading(message, { delayMs, minShowMs });
  try {
    await new Promise((r) =>
      typeof requestAnimationFrame !== "undefined"
        ? requestAnimationFrame(() => requestAnimationFrame(r))
        : setTimeout(r, 0)
    );
    return await asyncFn();
  } finally {
    stopLoading(token);
  }
}

export { START_EVENT, STOP_EVENT, DEFAULT_DELAY_MS, DEFAULT_MIN_SHOW_MS };
